import { Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { ConditionalRender } from '@ui';
import { MdOutlineLocationOn } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import SectionRow from '@features/components/CaseOverview/components/SectionRow/SectionRow';
import { SignatureRow } from '@features/components/CaseOverview/components/ServiceOutcome/SignatureRow';
import { ConfirmationSection } from './ConfirmationSection';
import { ClinicalData, ClinicalItemType, ClinicalServiceConfirmation, OrganType, Randomization } from '@/api/apis/case';
import { formatTimestamp, getClinicalRecordByType, isRandomizationScs } from '@/utils/helpers';
import { inventoryTypeNames } from './constants';
import { OCSSupply } from './OCSSupply';
import { FacilityRow } from '../FacilityRow';

import { getProvidedServicesText } from './utils';

type ServiceConfirmationModalProps = {
  confirmationData: ClinicalServiceConfirmation;
  isOpen: boolean;
  onOpenChange: () => void;
  unosId: string;
  organType: OrganType;
  clinicalData: ClinicalData;
  isTrialCase: boolean;
  randomization?: Randomization;
  additionalServices?: boolean;
};

export const ServiceConfirmationModal = ({
  onOpenChange,
  isOpen,
  confirmationData,
  unosId,
  organType,
  clinicalData,
  isTrialCase,
  randomization,
  additionalServices,
}: ServiceConfirmationModalProps) => {
  const {
    retrievalSurgery,
    retrievalSurgeon,
    ocsManagement,
    ocsSpecialist,
    txRepresentative,
    providedServiceTypes,
    ocsSupplies,
  } = confirmationData;
  const offSCS = getClinicalRecordByType(clinicalData, ClinicalItemType.OffSCS);
  const scsTermination = getClinicalRecordByType(clinicalData, ClinicalItemType.ScsTerminationOption);
  const organTakenOffScs = !!(offSCS || scsTermination);
  const isScs = isRandomizationScs(randomization);
  const hideOutcome = organTakenOffScs && isTrialCase;

  return (
    <Modal
      className="scroll"
      scrollBehavior="outside"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      closeButton={<IoClose size={42} className="z-1000" />}
      classNames={{
        closeButton: 'text-base-10 mt-6 mr-4',
        header: 'text-hl! flex flex-col gap-4 text-base-20 font-secondary pt-6 pb-4',
        body: 'flex flex-col mx-6 p-0 pb-6 gap-6 shadow-inset-l',
        footer: 'pb-6',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <div>Service Confirmation Form</div>
          <Divider />
        </ModalHeader>

        <ModalBody>
          <ConfirmationSection title="Retrieval Surgery" checked={!!retrievalSurgeon?.signature.fullName}>
            <SectionRow label="UNOS ID">{unosId}</SectionRow>
            <SectionRow label="Organ Type">{organType}</SectionRow>
            <SectionRow label="Surgery Date">
              {formatTimestamp(retrievalSurgery?.surgeryDate, retrievalSurgery?.surgeryDateTimeZone)}
            </SectionRow>
            <SectionRow label="Donor Hospital">
              <FacilityRow
                name={retrievalSurgery?.donorHospital.name}
                logo={retrievalSurgery?.donorHospital.logoPath}
              />
            </SectionRow>
            <SectionRow label="Retrieval Surgeon">{retrievalSurgeon?.signature.fullName}</SectionRow>
            <SectionRow label="Confirmation Signed">
              <SignatureRow
                timestamp={retrievalSurgeon?.timestamp}
                timeZone={retrievalSurgeon?.timeZone}
                filePath={retrievalSurgeon?.signature.file.path}
              />
            </SectionRow>
          </ConfirmationSection>
          <ConfirmationSection title="OCS Management" checked={!!ocsSpecialist?.signature.fullName}>
            <SectionRow label="Service Date">
              {formatTimestamp(ocsManagement?.serviceDate, ocsManagement?.serviceDateTimeZone)}
            </SectionRow>
            <SectionRow label="Service Outcome">{hideOutcome ? '' : ocsManagement?.serviceOutcomeType}</SectionRow>
            <SectionRow label="Transplant Center">
              <FacilityRow name={ocsManagement?.txCenter.name} logo={ocsManagement?.txCenter.logoPath} />
            </SectionRow>
            <SectionRow label="OCS Specialist">{ocsSpecialist?.signature.fullName}</SectionRow>
            <SectionRow label="Confirmation Signed">
              <SignatureRow
                timestamp={ocsSpecialist?.timestamp}
                timeZone={ocsSpecialist?.timeZone}
                filePath={ocsSpecialist?.signature.file.path}
              />
            </SectionRow>
          </ConfirmationSection>
          <ConfirmationSection title="TX Delegate" checked={!!txRepresentative?.signature.fullName}>
            <SectionRow label="Delegate">{txRepresentative?.signature.fullName}</SectionRow>
            <SectionRow label="Credentials">{txRepresentative?.signature.credentials}</SectionRow>
            <SectionRow label="Confirmation Signed">
              <SignatureRow
                timestamp={txRepresentative?.timestamp}
                timeZone={txRepresentative?.timeZone}
                filePath={txRepresentative?.signature.file.path}
              />
            </SectionRow>
          </ConfirmationSection>
          <ConditionalRender show={(!!providedServiceTypes || additionalServices) && !isTrialCase}>
            <ConfirmationSection title="Service provided by Transmedics">
              <SectionRow label="Services Provided" truncate={false}>
                {getProvidedServicesText(providedServiceTypes, additionalServices)}
              </SectionRow>
            </ConfirmationSection>
          </ConditionalRender>
          <ConditionalRender show={!!ocsSupplies}>
            <ConfirmationSection
              title="OCS Supplies"
              rightElement={
                <div className=" flex gap-1 items-center text-m-r text-base-20">
                  <MdOutlineLocationOn size={20} />
                  <div>{ocsSupplies?.ocsConsole?.locationOfOrigin}</div>
                </div>
              }
            >
              <div className="grid grid-cols-2 gap-2 justify-between">
                {!isScs && (
                  <OCSSupply
                    type="device"
                    name="OCS Console"
                    description={ocsSupplies?.ocsConsole?.fullSN || ocsSupplies?.ocsConsole?.sn}
                  />
                )}
                {ocsSupplies?.inventory.map((i) => (
                  <OCSSupply key={i.id} type="supply" name={inventoryTypeNames[i.type]} description={i.serialCode} />
                ))}
              </div>
            </ConfirmationSection>
          </ConditionalRender>
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};
